import { useState, useEffect, useMemo } from "react"
import { fetchUserActivity } from "@/lib/community/community-api"
import type { ActivityEvent, ActivityEventType } from "@/lib/community/types"

const EVENT_ICONS: Record<ActivityEventType, string> = {
  publish: "📤",
  copy: "🔄",
  like: "❤️",
  tip: "💰",
  follow: "👥",
  badge: "🏆",
  rating: "⭐",
}

function formatRelativeTime(dateStr: string) {
  const now = Date.now()
  const diff = now - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return "刚刚"
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 7) return `${days}天前`
  const date = new Date(dateStr)
  return `${date.getMonth() + 1}月${date.getDate()}日`
}

function renderEventText(event: ActivityEvent) {
  switch (event.type) {
    case "publish":
      return (
        <span>
          发布了新作品
          {event.target && <span className="font-medium"> 「{event.target.title}」</span>}
        </span>
      )
    case "copy":
      return (
        <span>
          <span className="font-medium">{event.actor.displayName}</span> 复制了
          {event.target && <span className="font-medium"> 「{event.target.title}」</span>}
        </span>
      )
    case "like":
      return (
        <span>
          <span className="font-medium">{event.actor.displayName}</span> 点赞了
          {event.target && <span> 「{event.target.title}」</span>}
        </span>
      )
    case "tip":
      return (
        <span>
          <span className="font-medium">{event.actor.displayName}</span> 打赏了 ¥{event.amount}
          {event.target && <span> 「{event.target.title}」</span>}
        </span>
      )
    case "follow":
      return (
        <span>
          <span className="font-medium">{event.actor.displayName}</span> 关注了你
        </span>
      )
    case "badge":
      return <span>{event.message || "获得新勋章"}</span>
    case "rating":
      return (
        <span>
          <span className="font-medium">{event.actor.displayName}</span> 给了 {event.amount} 星好评
          {event.target && <span> 「{event.target.title}」</span>}
        </span>
      )
  }
}

interface ProfileRecentActivitySectionProps {
  userId: string
}

export function ProfileRecentActivitySection({ userId }: ProfileRecentActivitySectionProps) {
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchUserActivity(userId, 1).then((data) => {
      if (!cancelled) {
        setEvents(data.events)
        setLoading(false)
      }
    }).catch(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [userId])

  // Group events by date
  const grouped = useMemo(() => {
    const result: Record<string, ActivityEvent[]> = {}
    for (const event of events) {
      const d = new Date(event.createdAt)
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      let label: string
      if (d.toDateString() === today.toDateString()) label = "今天"
      else if (d.toDateString() === yesterday.toDateString()) label = "昨天"
      else label = `${d.getMonth() + 1}月${d.getDate()}日`

      if (!result[label]) result[label] = []
      result[label].push(event)
    }
    return result
  }, [events])

  return (
    <section className="profile-section">
      <div>
        {/* Header */}
        <div className="profile-section-header">
          <h2 className="profile-section-title">最新动态</h2>
        </div>

        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 rounded-xl bg-muted/30 animate-pulse" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="profile-empty-state">
            <span className="profile-empty-state-icon">📭</span>
            <p className="profile-empty-state-title">还没有动态记录</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([day, dayEvents]) => (
              <div key={day}>
                <h3 className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider mb-4 font-mono">
                  {day}
                </h3>
                <div className="relative pl-6 border-l-2 border-border/50 space-y-3">
                  {dayEvents.map((event) => (
                    <div
                      key={event.id}
                      className="relative liquid-glass-card rounded-xl p-4 flex items-center gap-4 transition-all hover:-translate-y-px"
                    >
                      {/* Timeline dot */}
                      <div className="absolute -left-[calc(1.5rem+5px)] w-2.5 h-2.5 rounded-full bg-primary/60 ring-4 ring-background" />
                      <div className="text-base shrink-0">{EVENT_ICONS[event.type]}</div>
                      <div className="flex-1 min-w-0 text-sm leading-relaxed">
                        {renderEventText(event)}
                      </div>
                      <div className="text-xs text-muted-foreground/60 shrink-0 font-mono">
                        {formatRelativeTime(event.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
